import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { FLOW_GROUP_NAMES, flowGradeColour } from "../constants/flowPresentation";
import { MOTION_DURATION, MOTION_EASE } from "../../../shared/constants/motion";

const ids = ["1", "2", "3", "4", "5", "6"];

export default function FlowLiveSummary({ grades, coreData, total, completed }) {
  const [open, setOpen] = useState(false);
  const [footerVisible, setFooterVisible] = useState(false);
  const [availableHeight, setAvailableHeight] = useState(null);
  const container = useRef(null);
  const pendingJump = useRef(null);
  const reduceMotion = useReducedMotion();
  useEffect(() => {
    const footer = document.querySelector(".site-footer");
    if (!footer || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      setFooterVisible(entry.isIntersecting);
    }, { rootMargin: "0px 0px 48px 0px", threshold: 0 });
    observer.observe(footer);
    return () => observer.disconnect();
  }, []);
  useLayoutEffect(() => {
    if (!open) return undefined;
    let frame;
    const measure = () => {
      const node = container.current;
      if (!node) return;
      const dockTop = node.parentElement.getBoundingClientRect().top;
      const bottomGap = parseFloat(getComputedStyle(node).bottom) || 0;
      setAvailableHeight(Math.max(0, Math.min(window.visualViewport?.height ?? window.innerHeight, dockTop) - bottomGap - 96));
    };
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    window.visualViewport?.addEventListener("resize", schedule);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      window.visualViewport?.removeEventListener("resize", schedule);
    };
  }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    const closeOutside = event => { if (!container.current?.contains(event.target)) setOpen(false); };
    const closeEscape = event => { if (event.key === "Escape") setOpen(false); };
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("keydown", closeEscape);
    return () => { document.removeEventListener("pointerdown", closeOutside); document.removeEventListener("keydown", closeEscape); };
  }, [open]);
  const scrollToSection = id => {
    document.getElementById(id)?.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "start" });
  };
  const jump = id => {
    if (open) {
      pendingJump.current = id;
      setOpen(false);
    } else {
      pendingJump.current = null;
      scrollToSection(id);
    }
  };
  const finishClose = () => {
    if (!pendingJump.current) return;
    const id = pendingJump.current;
    pendingJump.current = null;
    scrollToSection(id);
  };
  return (
    <aside ref={container} className={`live-summary${open ? " expanded" : ""}${footerVisible ? " is-footer-hidden" : ""}`} aria-label="Live diploma summary" aria-hidden={footerVisible || undefined} inert={footerVisible} style={availableHeight ? { "--summary-space": `${availableHeight}px` } : undefined}>
      <div className="summary-inner">
        <p><output id="live-total">{total}</output><span>/ 45</span></p>
        <nav className="summary-nav" aria-label="Subject navigation">{ids.map(id => <button key={id} className="summary-group" type="button" onClick={() => jump(`g${id}`)} aria-label={`Jump to Group ${id}`} style={{ "--summary-colour": flowGradeColour(grades[id]?.grade) }}><span>G{id}</span><strong>{grades[id]?.grade ?? "--"}</strong><span className="summary-group-track" aria-hidden="true"><span style={{ width: `${(grades[id]?.grade || 0) / 7 * 100}%` }} /></span></button>)}</nav>
        <div className="summary-progress"><span>{completed} of 6 subjects complete</span><span className="completion-track" aria-hidden="true"><span id="completion-fill" style={{ width: `${completed / 6 * 100}%`, background: flowGradeColour(total / 45 * 7) }} /></span></div>
        <button className="summary-core" type="button" onClick={() => jump("core-title")}>{coreData.corePoints == null ? "Core: --" : `Core: +${coreData.corePoints}`}</button>
        <button className="summary-toggle" type="button" aria-expanded={open} aria-controls="live-subject-breakdown" aria-label={open ? "Hide subject breakdown" : "Show subject breakdown"} onClick={() => { pendingJump.current = null; setOpen(value => !value); }}><span>Breakdown</span><svg width="12" height="12" viewBox="0 0 12 12" fill="none" className={open ? "is-open" : ""} aria-hidden="true"><path d="m3 4.5 3 3 3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg></button>
      </div>
      <AnimatePresence initial={false} onExitComplete={finishClose}>{open && <motion.div id="live-subject-breakdown" className="summary-disclosure" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: reduceMotion ? 0 : MOTION_DURATION.base, ease: MOTION_EASE.snap }}>
        <div className="summary-details">{ids.map(id => <button key={id} type="button" className="summary-detail" onClick={() => jump(`g${id}`)}><span className="summary-detail-grade" style={{ color: flowGradeColour(grades[id]?.grade) }}>{grades[id]?.grade ?? "--"}</span><span><span className="summary-detail-title">G{id} <span>{grades[id]?.subject || FLOW_GROUP_NAMES[Number(id) - 1]}</span>{grades[id]?.level && <span className="summary-detail-level">{grades[id].level}</span>}</span><span className="summary-detail-note">{grades[id]?.neededText || (grades[id]?.grade === 7 ? "Grade 7" : "Enter your marks")}</span></span></button>)}</div>
        <div className="summary-details-footer"><button type="button" onClick={() => jump("core-title")}>Core points: {coreData.corePoints ?? "--"} / 3</button><button type="button" onClick={() => jump("diploma-title")}>Your diploma total: {total} / 45</button></div>
      </motion.div>}</AnimatePresence>
    </aside>
  );
}
